import {
  Button,
  DateRangeScrubber,
  DateRangeScrubberBucketHoverContent,
  DateRangeScrubberRange,
  Skeleton,
  Typography,
} from '@compass/components'
import { Route } from '@routes/_authenticated/_layout/workspaces/$subscriptionId/bot-detection/events/index'
import { AppRoute } from 'appRoutes'
import { AppliedQueryFilters, QueryFilterChangeHandler, QueryFilterMenu } from 'components/QueryFilters'
import {
  DateRangeContextProvider,
  PersistentDateRangePicker,
  SetHeader,
  SubHeader,
  useDateRangeContext,
  useDefaultPredefinedRanges,
} from 'features/commonUI'
import { isPredefinedRange } from 'helpers/dateRange'
import { pluralize } from 'helpers/pluralize'
import { useLogPageView } from 'helpers/vendor'
import { useCurrentUser } from 'hooks/api/users'
import { useBotDetectionAccess } from 'hooks/useBotDetectionAccess'
import { DateTime, Duration } from 'luxon'
import { ampli } from 'models/ampli'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { BOT_IDENTITY_COLORS, BOT_IDENTITY_LABELS } from '../consts'
import {
  BOT_DETECTION_EDGE_MAX_RANGE_DAYS,
  BOT_DETECTION_EDGE_PREDEFINED_DATE_RANGE_IDS,
  BOT_DETECTION_EDGE_SCRUBBER_RANGE_DAYS,
  BOT_DETECTION_PREDEFINED_DATE_RANGE_IDS,
} from '../dateRanges'
import { useBotDetectionEvents, useBotDetectionEventsTimeline } from '../hooks'
import { BotIdentity } from '../models'
import { BotDetectionEventsActions } from './BotDetectionEventsActions'
import { BotDetectionEventsTable } from './BotDetectionEventsTable/BotDetectionEventsTable'
import { CreateBotDetectionCellFilter } from './BotDetectionEventsTable/types'
import { ExportBotDetectionEventsModal } from './ExportBotDetectionEventsModal'
import {
  BotDetectionEventsFilterValues,
  getActiveBotDetectionEventsFilters,
  getAppliedBotDetectionEventsFilterCount,
  getClearedBotDetectionEventsFilters,
  useBotDetectionEventsFilter,
} from './useBotDetectionEventsFilter'
import {
  BotDetectionScrubberBucket,
  formatScrubberBucketRange,
  formatScrubberBucketTimestamp,
  getBotDetectionEdgeWindowStart,
  getExactPredefinedRange,
  getRouteDateRange,
  getSpoofedBotBucketFill,
  isBotDetectionEventsPredefinedRangeId,
} from './utils'

const DEFAULT_BOT_DETECTION_EVENTS_QUERY_LIMIT = 20
const BOT_IDENTITY_ORDER: BotIdentity[] = ['spoofed', 'unknown', 'signed', 'verified']

export function BotDetectionEventsPage() {
  return (
    <DateRangeContextProvider>
      <Page />
    </DateRangeContextProvider>
  )
}

function Page() {
  const { subscriptionId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [scrubberPreviewRange, setScrubberPreviewRange] = useState<DateRangeScrubberRange>()
  const { isLoading: isCurrentUserLoading } = useCurrentUser()

  const botDetectionAccess = useBotDetectionAccess()
  const shouldFetchBotDetectionData = botDetectionAccess.hasAiApiAccess
  const { timeZone, today } = useDateRangeContext()
  const { ranges } = useDefaultPredefinedRanges(BOT_DETECTION_PREDEFINED_DATE_RANGE_IDS)
  const maxRangeDays = BOT_DETECTION_EDGE_MAX_RANGE_DAYS
  const edgeWindowStart = useMemo(
    () => getBotDetectionEdgeWindowStart(today, BOT_DETECTION_EDGE_MAX_RANGE_DAYS),
    [today]
  )
  const scrubberWindowStart = useMemo(
    () => getBotDetectionEdgeWindowStart(today, BOT_DETECTION_EDGE_SCRUBBER_RANGE_DAYS),
    [today]
  )
  const dateRange = useMemo(
    () => getRouteDateRange(search, ranges, edgeWindowStart.startOf('day')),
    [edgeWindowStart, ranges, search]
  )
  useEffect(() => {
    if (!dateRange.wasClamped) {
      return
    }

    navigate({
      search: (old) => ({
        ...old,
        period: undefined,
        since: dateRange.since.toISO() ?? undefined,
        before: dateRange.before.toISO() ?? undefined,
        aggregateRange: true,
      }),
      replace: true,
    })
  }, [dateRange, navigate])
  const scrubberHistoricalRange = useMemo(
    () => ({ startDate: scrubberWindowStart, endDate: ranges.today.startDate.minus({ milliseconds: 1 }) }),
    [ranges.today.startDate, scrubberWindowStart]
  )
  const scrubberDomain = useMemo(
    () => ({ startDate: scrubberWindowStart, endDate: ranges.today.endDate }),
    [ranges.today.endDate, scrubberWindowStart]
  )

  const scrubberSelection = useMemo(() => {
    const range = scrubberPreviewRange ?? { startDate: dateRange.since, endDate: dateRange.before }

    return {
      startDate: DateTime.max(range.startDate, scrubberDomain.startDate),
      endDate: DateTime.min(range.endDate, scrubberDomain.endDate),
    }
  }, [dateRange.before, dateRange.since, scrubberDomain.endDate, scrubberDomain.startDate, scrubberPreviewRange])
  const definedRanges = useMemo(() => {
    return BOT_DETECTION_EDGE_PREDEFINED_DATE_RANGE_IDS.map((rangeId) => ranges[rangeId])
  }, [ranges])
  const filterValues = getActiveBotDetectionEventsFilters(search)
  const historicalTimelineQuery = useBotDetectionEventsTimeline({
    subscriptionId,
    start: scrubberHistoricalRange.startDate,
    end: scrubberHistoricalRange.endDate,
    period: 'hour',
    source: 'edge',
    enabled: !isCurrentUserLoading && shouldFetchBotDetectionData,
  })
  const todayTimelineQuery = useBotDetectionEventsTimeline({
    subscriptionId,
    start: ranges.today.startDate,
    end: ranges.today.endDate,
    period: 'hour',
    source: 'edge',
    enabled: !isCurrentUserLoading && shouldFetchBotDetectionData,
  })
  const scrubberSelectedPredefinedRange = useMemo(
    () => getExactPredefinedRange(scrubberSelection, ranges),
    [ranges, scrubberSelection]
  )
  const scrubberBuckets = useMemo<BotDetectionScrubberBucket[]>(
    () =>
      [...(historicalTimelineQuery.data?.buckets ?? []), ...(todayTimelineQuery.data?.buckets ?? [])].map((bucket) => ({
        startDate: DateTime.fromISO(bucket.start, { zone: timeZone }),
        endDate: DateTime.fromISO(bucket.end, { zone: timeZone }),
        count: bucket.totalCount,
        data: bucket.identityBreakdown,
      })) ?? [],
    [historicalTimelineQuery.data, timeZone, todayTimelineQuery.data]
  )
  const getScrubberBucketHoverContent = useCallback(
    (bucket: BotDetectionScrubberBucket): DateRangeScrubberBucketHoverContent => {
      const range = formatScrubberBucketRange(bucket)
      const count = pluralize(bucket.count, 'event')

      return {
        content: (
          <div className='grid gap-2 px-3 py-2'>
            <div className='grid gap-1.5'>
              {BOT_IDENTITY_ORDER.map((identity) => (
                <div key={identity} className='flex items-center justify-between gap-6'>
                  <span className='flex min-w-0 items-center gap-2'>
                    <span
                      className={`size-2 rounded-xs border border-black/10 ${BOT_IDENTITY_COLORS[identity].legendMarker}`}
                    />
                    <Typography size='xs' className='text-gray-800 whitespace-nowrap'>
                      {BOT_IDENTITY_LABELS[identity]}
                    </Typography>
                  </span>
                  <Typography variant='code' size='xs' weight='medium'>
                    {bucket.data[identity].toLocaleString()}
                  </Typography>
                </div>
              ))}
            </div>
            <Typography size='xs' className='text-gray-900 whitespace-nowrap'>
              {formatScrubberBucketTimestamp(bucket)}
            </Typography>
          </div>
        ),
        ariaLabel: `${count}, ${range}`,
      }
    },
    []
  )
  const limit = search.limit ?? DEFAULT_BOT_DETECTION_EVENTS_QUERY_LIMIT
  const edgeQuery = useBotDetectionEvents({
    subscriptionId,
    limit,
    since: dateRange.since,
    before: dateRange.before,
    source: 'edge',
    enabled: shouldFetchBotDetectionData,
    ...filterValues,
  })
  const query = edgeQuery
  const totalCount = edgeQuery.data?.pages[0]?.totalHits
  const loadedEvents = useMemo(() => query.data?.pages.flatMap((page) => page.events) ?? [], [query.data])
  const { filterDefinitions, filterMenuLayout } = useBotDetectionEventsFilter({
    subscriptionId,
    events: loadedEvents,
    filterValues,
  })
  const appliedFilterCount = getAppliedBotDetectionEventsFilterCount(filterValues)
  const hasEvents = loadedEvents.length > 0
  const isExportDisabled = query.isFetching || query.isError || !hasEvents
  const handleFilterChange = useCallback<QueryFilterChangeHandler<BotDetectionEventsFilterValues>>(
    (nextFilters) => {
      ampli.attributeFilterApplied({
        datasetFocus: 'Bot Detection',
        attributeFilters: Object.keys(nextFilters),
        filterType: 'Dropdown',
      })
      navigate({
        search: (old) => ({ ...old, ...nextFilters }),
      })
    },
    [navigate]
  )
  const handleClearFilters = useCallback(() => {
    navigate({
      search: (old) => ({
        ...old,
        ...getClearedBotDetectionEventsFilters(filterDefinitions),
      }),
    })
  }, [filterDefinitions, navigate])
  const handleScrubberChangeEnd = useCallback(
    (range: DateRangeScrubberRange) => {
      const matchingRange = getExactPredefinedRange(range, ranges)
      const matchingPeriod = isBotDetectionEventsPredefinedRangeId(matchingRange?.id) ? matchingRange.id : undefined

      navigate({
        search: (old) => ({
          ...old,
          period: matchingPeriod,
          since: matchingPeriod ? undefined : (range.startDate.toISO() ?? undefined),
          before: matchingPeriod ? undefined : (range.endDate.toISO() ?? undefined),
          aggregateRange: matchingPeriod ? undefined : true,
        }),
      })
    },
    [navigate, ranges]
  )
  const createCellFilter = useCallback<CreateBotDetectionCellFilter>(
    (nextFilters) => () => {
      navigate({
        search: (old) => ({ ...old, ...nextFilters }),
      })
    },
    [navigate]
  )
  useEffect(() => {
    setScrubberPreviewRange(undefined)
  }, [dateRange.before, dateRange.since])

  useLogPageView(() =>
    ampli.dataWithFiltersAppliedViewed({
      datasetFocus: 'Bot Detection',
      'App Page Path': AppRoute.BotDetectionEvents,
      periodLengthDays: Math.round(dateRange.before.diff(dateRange.since, 'days').days),
    })
  )

  return (
    <>
      <SetHeader
        title='Events'
        breadcrumb={[
          {
            title: 'Bots & AI agents',
            to: AppRoute.BotDetection,
            params: { subscriptionId },
          },
        ]}
      />
      <>
        <SubHeader
          keepRowLayout
          title={
            <Typography variant='heading' size='2xl' as='h2'>
              Events
            </Typography>
          }
          description='Events recorded with Automation Intelligence API. Select a row for details.'
          actions={
            <BotDetectionEventsActions
              // TODO: re-enable once export moves to a dedicated Agent traffic API call — these are
              // edge events and can't go through the current identification export endpoint.
              showExport={false}
              isExportDisabled={isExportDisabled}
              onExport={() => {
                ampli.exportAsCsvClicked()
                setIsExportModalOpen(true)
              }}
            />
          }
        />
        <div data-testid='bot-detection-events-page' className='flex flex-col gap-4'>
          {timeZone && (
            <div className='-mx-4 md:-mx-6'>
              <DateRangeScrubber
                startDate={scrubberDomain.startDate}
                endDate={scrubberDomain.endDate}
                selectedStartDate={scrubberSelection.startDate}
                selectedEndDate={scrubberSelection.endDate}
                selectedRangeLabel={scrubberSelectedPredefinedRange?.label}
                buckets={scrubberBuckets}
                granularity='hour'
                resizableWindow
                minSelectableStartDate={edgeWindowStart}
                minSelectableStartTooltip='No past data'
                rangeContainerClassName='mx-4 md:mx-6'
                axisClassName='-left-4 -right-4 md:-left-6 md:-right-6'
                bucketHoverAriaLabel='Bot detection events by hour'
                getBucketHoverContent={getScrubberBucketHoverContent}
                getBucketFill={getSpoofedBotBucketFill}
                onChange={setScrubberPreviewRange}
                onChangeEnd={handleScrubberChangeEnd}
              />
            </div>
          )}
          <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
            <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
              <QueryFilterMenu
                appliedFilterCount={appliedFilterCount}
                filterMenuLayout={filterMenuLayout}
                filterDefinitions={filterDefinitions}
                filterValues={filterValues}
                onChange={handleFilterChange}
                onOpen={() => ampli.attributeFilterDialogOpened({ datasetFocus: 'Bot Detection' })}
                filterItemTestId='bot-detection-events-query-filter-item'
                renderOptionsAsSubmenu
              />
              {timeZone ? (
                <PersistentDateRangePicker
                  timezone={timeZone}
                  route={AppRoute.BotDetectionEvents}
                  routeParams={{ subscriptionId }}
                  definedRanges={definedRanges}
                  defaultDateRange={ranges.today}
                  initialMinDate={edgeWindowStart.startOf('day')}
                  initialMaxDate={DateTime.now().endOf('day')}
                  maxRange={Duration.fromDurationLike({
                    days: maxRangeDays,
                  })}
                  onOpen={() => ampli.dateFilterSelectorOpened({ datasetFocus: 'Bot Detection' })}
                  onApplyRange={(range) => {
                    ampli.dateFilterApplied({
                      datasetFocus: 'Bot Detection',
                      dateRange: isPredefinedRange(range) ? (range.label as any) : 'Other',
                      periodLengthDays: Math.round(range.endDate.diff(range.startDate, 'days').days),
                    })
                  }}
                />
              ) : (
                <Skeleton className='h-8 w-20' />
              )}
            </div>
            {appliedFilterCount > 0 && (
              <Button
                variant='ghost'
                tone='background2'
                alt
                className='hidden md:flex'
                onPress={handleClearFilters}
                aria-label='Clear filters'
              >
                Clear
              </Button>
            )}
          </div>
          <AppliedQueryFilters
            dataTestId='bot-detection-events-applied-filters'
            filterDefinitions={filterDefinitions}
            filterValues={filterValues}
            onChange={handleFilterChange}
            filterItemTestId='bot-detection-events-query-filter-item'
          />
          {appliedFilterCount > 0 && (
            <Button variant='ghost' className='md:hidden' onPress={handleClearFilters}>
              Clear
            </Button>
          )}
          <BotDetectionEventsTable
            subscriptionId={subscriptionId}
            totalCount={totalCount}
            query={query}
            limit={limit}
            createCellFilter={createCellFilter}
            appliedFilterCount={appliedFilterCount}
            onClear={handleClearFilters}
          />
        </div>
      </>
      <ExportBotDetectionEventsModal
        isOpen={isExportModalOpen}
        handleClose={() => setIsExportModalOpen(false)}
        subscriptionId={subscriptionId}
        dateRange={dateRange}
        filterDefinitions={filterDefinitions}
        filterValues={filterValues}
      />
    </>
  )
}
